/**
 * "View guidelines" menu action (PA form variant): analytics + opens NCCN guidelines flow.
 * Driven by Postgres-backed `generalOrderDetails` (`GeneralOrderDetails`).
 *
 * @module paFormThreeDotsMenu/handleViewGuidelines
 */
import { Item } from "../../../../data-model/documentModalWithSideBav";
import { GeneralOrderDetails } from "../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { handleViewNccnGuidelines } from "../../../../pages/medicalPaForm/components/utils/handleViewNccnGuidelines";
import { AppDispatch } from "../../../../redux/store/store";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";

/** Parameters for {@link handleViewGuidelines}. */
export interface HandleViewGuidelinesParams {
  dispatch: AppDispatch;
  generalOrderDetails: GeneralOrderDetails;
  items: Item[];
  orgId: string;
  userId: string;
  userEmail: string;
  payerName: string;
  screenName: ScreenNamesForBigQuery;
}

/**
 * Handles the "View Guidelines" action for the PA form three-dots menu.
 * Opens the NCCN guidelines modal and logs analytics events sourced from
 * the live `singleMedicalPaOrder` payload.
 *
 * @param params - Parameters for the action
 */
export const handleViewGuidelines = ({
  dispatch,
  generalOrderDetails,
  items,
  orgId,
  userId,
  userEmail,
  payerName,
  screenName,
}: HandleViewGuidelinesParams): void => {
  const patientId =
    generalOrderDetails?.demographics?.patient_id ??
    generalOrderDetails?.demographics?.patient_mrn ??
    "";
  const orderId = generalOrderDetails?.order_id ?? "";

  apiWorkerManager.enqueueAndProcess({
    patient_id: patientId,
    order_id: orderId,
    org_id: orgId ?? "",
    event_name: EventsName.VIEW_GUIDELINES_CLICKED,
    user_id: userId,
    user_email: userEmail,
    additional_data: {
      screen_name: screenName,
      payer_name: payerName,
    },
  });

  handleViewNccnGuidelines(dispatch, items, {
    event_name: EventsName.VIEW_NCCN_GUIDELINES_CLICKED,
    patient_id: patientId,
    order_id: orderId,
    org_id: orgId ?? "",
    user_id: userId,
    user_email: userEmail,
    additional_data: {
      screen_name: screenName,
      payer_name: payerName,
    },
  });
};
